import type { PlanSummary } from '@esportsbudget/shared';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  BellRing,
  Calendar,
  Check,
  FileBarChart,
  Landmark,
  PlayCircle,
  Users,
  Wallet,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { LogoFullOnDark } from '@/components/brand/Logo';
import { LocaleToggle, ThemeToggle } from '@/components/layout/Toggles';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useFormatters } from '@/lib/format';
import { useDemoSession } from '@/lib/useDemoSession';
import { useAuth } from '@/providers/AuthProvider';

/**
 * Публичный лендинг (Фаза 14).
 *
 * Живёт в том же приложении отдельным маршрутом: свой сайт означал бы лишний
 * деплой, лишний домен и скриншоты, которые разъедутся с реальным интерфейсом.
 *
 * Тарифы тянутся из того же API, что и админка, — цены на витрине не могут
 * разойтись с тем, что реально настроено.
 */
export function LandingPage() {
  const { t } = useTranslation(['landing', 'auth', 'common']);
  const format = useFormatters();
  const { config } = useAuth();
  const demo = useDemoSession();

  const plansQuery = useQuery({
    queryKey: ['public-plans'],
    queryFn: () => api.get<PlanSummary[]>('/plans/public', undefined, { skipWorkspace: true }),
  });

  // Заголовок вкладки и описание для превью ссылки — задаём здесь, потому что
  // на статическую разметку рассчитывать нельзя: приложение одностраничное.
  useEffect(() => {
    const previous = document.title;
    document.title = t('landing:metaTitle');

    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute('content') ?? '';
    description?.setAttribute('content', t('landing:metaDescription'));

    return () => {
      document.title = previous;
      description?.setAttribute('content', previousDescription);
    };
  }, [t]);

  const canRegister = config?.registrationMode !== 'CLOSED';
  const inviteOnly = config?.registrationMode === 'INVITE_ONLY';

  return (
    <div className="min-h-screen bg-bg">
      <header className="sticky top-0 z-20 border-b border-border bg-surface/90 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-[1080px] items-center gap-3 px-4">
          <LogoFullOnDark className="h-5 w-auto" />

          <nav className="ml-6 hidden gap-4 text-[13.5px] text-muted md:flex">
            <a href="#features" className="hover:text-text">
              {t('landing:navFeatures')}
            </a>
            <a href="#taxes" className="hover:text-text">
              {t('landing:navTaxes')}
            </a>
            <a href="#pricing" className="hover:text-text">
              {t('landing:navPricing')}
            </a>
            <a href="#faq" className="hover:text-text">
              {t('landing:navFaq')}
            </a>
          </nav>

          <div className="ml-auto flex items-center gap-1">
            <LocaleToggle />
            <ThemeToggle />
            <Link
              to="/login"
              className="ml-1 rounded-lg px-3 py-1.5 text-[13.5px] font-medium text-muted hover:bg-surface-2 hover:text-text"
            >
              {t('auth:signIn')}
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* Первый экран */}
        <section className="mx-auto max-w-[1080px] px-4 pb-14 pt-16 text-center sm:pt-24">
          <LogoFullOnDark className="h-8 w-auto sm:h-9" />

          <h1 className="mx-auto mt-4 max-w-[720px] text-[32px] font-semibold leading-[1.15] tracking-tight sm:text-[44px]">
            {t('landing:heroTitle')}
          </h1>

          <p className="mx-auto mt-4 max-w-[560px] text-[15px] leading-relaxed text-muted sm:text-[16px]">
            {t('landing:heroSubtitle')}
          </p>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-2.5">
            {config?.demoEnabled && (
              <Button
                size="lg"
                icon={<PlayCircle />}
                loading={demo.isPending}
                onClick={() => demo.mutate()}
              >
                {t('auth:tryDemo')}
              </Button>
            )}
            {canRegister && (
              <Link to="/register">
                <Button size="lg" variant="secondary" iconRight={<ArrowRight />}>
                  {/* При закрытой регистрации «Начать бесплатно» обещает то,
                      чего человек не получит: нужен код приглашения. */}
                  {inviteOnly ? t('landing:haveInvite') : t('landing:startFree')}
                </Button>
              </Link>
            )}
          </div>

          <p className="mt-3 text-[12.5px] text-subtle">
            {config?.registrationMode === 'INVITE_ONLY'
              ? t('landing:inviteOnly')
              : t('landing:noCard')}
          </p>
        </section>

        {/* Проблема своими словами */}
        <section className="border-y border-border bg-surface-2/50 py-14">
          <div className="mx-auto max-w-[880px] px-4">
            <h2 className="text-center text-[22px] font-semibold tracking-tight sm:text-[26px]">
              {t('landing:problemTitle')}
            </h2>

            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              {['salary', 'advance', 'balance', 'debt'].map((key) => (
                <div
                  key={key}
                  className="rounded-[10px] border border-border bg-surface px-4 py-3.5 text-[13.5px] leading-relaxed text-muted"
                >
                  «{t(`landing:problem.${key}`)}»
                </div>
              ))}
            </div>

            <p className="mt-7 text-center text-[15px] leading-relaxed">
              {t('landing:problemAnswer')}
            </p>
          </div>
        </section>

        {/* Возможности */}
        <section id="features" className="mx-auto max-w-[1080px] px-4 py-16">
          <h2 className="text-center text-[22px] font-semibold tracking-tight sm:text-[26px]">
            {t('landing:featuresTitle')}
          </h2>

          <div className="mt-8 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            <Feature icon={<Users />} titleKey="people" />
            <Feature icon={<Wallet />} titleKey="wallets" />
            <Feature icon={<BellRing />} titleKey="reminders" />
            <Feature icon={<Calendar />} titleKey="calendar" />
            <Feature icon={<FileBarChart />} titleKey="reports" />
            <Feature icon={<Landmark />} titleKey="taxes" />
          </div>
        </section>

        {/* Налоги — то, чего нет у домашних бюджетов */}
        <section id="taxes" className="border-y border-border bg-surface-2/50 py-16">
          <div className="mx-auto max-w-[880px] px-4">
            <h2 className="text-center text-[22px] font-semibold tracking-tight sm:text-[26px]">
              {t('landing:taxTitle')}
            </h2>
            <p className="mx-auto mt-3 max-w-[620px] text-center text-[14.5px] leading-relaxed text-muted">
              {t('landing:taxSubtitle')}
            </p>

            <Card className="mt-7 overflow-x-auto" padded={false}>
              <table className="w-full min-w-[560px] text-[13.5px]">
                <thead className="border-b border-border text-left text-[12.5px] text-subtle">
                  <tr>
                    <th className="px-4 py-3 font-medium">{t('landing:taxMode')}</th>
                    <th className="px-4 py-3 font-medium">{t('landing:taxAccrued')}</th>
                    <th className="px-4 py-3 font-medium">{t('landing:taxPerson')}</th>
                    <th className="px-4 py-3 font-medium">{t('landing:taxOrg')}</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['ORG_PAYS', '100 000', '100 000', '106 000'],
                    ['PERSON_PAYS', '100 000', '94 000', '100 000'],
                    ['NO_TAX', '100 000', '100 000', '100 000'],
                  ].map(([mode, accrued, person, org]) => (
                    <tr key={mode} className="border-b border-border last:border-0">
                      <td className="px-4 py-3">
                        <p className="font-medium">{t(`landing:taxModeValue.${mode}`)}</p>
                        <p className="text-[12px] text-subtle">
                          {t(`landing:taxModeHint.${mode}`)}
                        </p>
                      </td>
                      <td className="tabular px-4 py-3">{accrued}</td>
                      <td className="tabular px-4 py-3">{person}</td>
                      <td className="tabular px-4 py-3 font-medium">{org}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            <p className="mt-3 text-center text-[12.5px] text-subtle">{t('landing:taxNote')}</p>
          </div>
        </section>

        {/* Тарифы */}
        <section id="pricing" className="mx-auto max-w-[1080px] px-4 py-16">
          <h2 className="text-center text-[22px] font-semibold tracking-tight sm:text-[26px]">
            {t('landing:pricingTitle')}
          </h2>
          <p className="mx-auto mt-3 max-w-[560px] text-center text-[14.5px] text-muted">
            {t('landing:pricingSubtitle')}
          </p>

          <div className="mt-8 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {(plansQuery.data ?? []).map((plan) => (
              <Card key={plan.id} className={plan.priceMinor === 0 ? 'border-accent/40' : undefined}>
                <div className="flex items-center gap-2">
                  <span className="text-[16px] font-semibold">{plan.name}</span>
                  {plan.isUnlimited && <Badge tone="accent">{t('landing:unlimited')}</Badge>}
                </div>

                {plan.description && (
                  <p className="mt-1.5 text-[12.5px] leading-relaxed text-muted">
                    {plan.description}
                  </p>
                )}

                <p className="mt-4 text-[24px] font-semibold leading-none">
                  {plan.priceMinor === 0
                    ? t('landing:free')
                    : format.money(plan.priceMinor, plan.currency)}
                  {plan.priceMinor > 0 && (
                    <span className="text-[13px] font-normal text-muted">
                      {' / '}
                      {t(`landing:period.${plan.billingPeriod}`)}
                    </span>
                  )}
                </p>

                <ul className="mt-4 space-y-1.5 border-t border-border pt-4 text-[13px]">
                  <Limit value={plan.maxWorkspaces} labelKey="limitWorkspaces" />
                  <Limit value={plan.maxPeoplePerWorkspace} labelKey="limitPeople" />
                  <Limit value={plan.maxWalletsPerWorkspace} labelKey="limitWallets" />
                  <Limit value={plan.maxRecurring} labelKey="limitRecurring" />
                  <Limit value={plan.maxStorageMb} labelKey="limitStorage" suffix=" МБ" />
                </ul>
              </Card>
            ))}
          </div>

          <p className="mt-5 text-center text-[12.5px] text-subtle">{t('landing:pricingNote')}</p>
        </section>

        {/* Вопросы */}
        <section id="faq" className="border-t border-border bg-surface-2/50 py-16">
          <div className="mx-auto max-w-[720px] px-4">
            <h2 className="text-center text-[22px] font-semibold tracking-tight sm:text-[26px]">
              {t('landing:faqTitle')}
            </h2>

            <div className="mt-7 space-y-2">
              {['email', 'data', 'security', 'crypto', 'currency'].map((key) => (
                <details
                  key={key}
                  className="group rounded-[10px] border border-border bg-surface px-4 py-3"
                >
                  <summary className="cursor-pointer list-none text-[14px] font-medium marker:hidden">
                    {t(`landing:faq.${key}.q`)}
                  </summary>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-muted">
                    {t(`landing:faq.${key}.a`)}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Последний призыв */}
        <section className="mx-auto max-w-[1080px] px-4 py-16 text-center">
          <h2 className="text-[22px] font-semibold tracking-tight sm:text-[26px]">
            {t('landing:ctaTitle')}
          </h2>
          <p className="mx-auto mt-3 max-w-[520px] text-[14.5px] text-muted">
            {t('landing:ctaSubtitle')}
          </p>

          <div className="mt-6 flex flex-wrap justify-center gap-2.5">
            {config?.demoEnabled && (
              <Button
                size="lg"
                icon={<PlayCircle />}
                loading={demo.isPending}
                onClick={() => demo.mutate()}
              >
                {t('auth:tryDemo')}
              </Button>
            )}
            <Link to="/login">
              <Button size="lg" variant="secondary">
                {t('auth:signIn')}
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-border py-8">
        <div className="mx-auto flex max-w-[1080px] flex-wrap items-center gap-3 px-4 text-[12.5px] text-subtle">
          <span>
            {t('common:appName')} — {t('landing:footerTagline')}
          </span>
          <Link to="/login" className="ml-auto hover:text-text">
            {t('auth:signIn')}
          </Link>
        </div>
      </footer>
    </div>
  );
}

function Feature({ icon, titleKey }: { icon: ReactNode; titleKey: string }) {
  const { t } = useTranslation('landing');

  return (
    <Card>
      <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-accent-soft text-accent [&>svg]:h-4 [&>svg]:w-4">
        {icon}
      </span>
      <p className="mt-3 text-[14.5px] font-semibold">{t(`feature.${titleKey}.title`)}</p>
      <p className="mt-1.5 text-[13px] leading-relaxed text-muted">
        {t(`feature.${titleKey}.body`)}
      </p>
    </Card>
  );
}

function Limit({
  value,
  labelKey,
  suffix = '',
}: {
  value: number | null;
  labelKey: string;
  suffix?: string;
}) {
  const { t } = useTranslation('landing');

  return (
    <li className="flex items-start gap-2">
      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" aria-hidden />
      <span className={cn(value === null && 'font-medium')}>
        {value === null
          ? t(`${labelKey}Unlimited`)
          : t(labelKey, { count: value, value: `${value}${suffix}` })}
      </span>
    </li>
  );
}
